import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCampaignSendMutation } from '@/client/api/campaigns/campaigns.mutation';
import {
  MessagingChannel,
  RecipientRef,
  SendResult,
} from '@/client/types/campaigns';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { smsLineLabel, useSmsLinesQuery } from '@/client/api/inbox/smsLines';

const ALL_CHANNELS: { id: MessagingChannel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'in_app', label: 'In-app' },
];

type RecipientMode = 'uid' | 'oids' | 'emails' | 'phones' | 'mixed';

const parseRecipients = (mode: RecipientMode, raw: string): RecipientRef[] => {
  const lines = raw
    .split(/[\n,]+/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (mode === 'uid') {
    return lines
      .map((l) => Number(l.replace(/^uid:/i, '').trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((uid) => ({ uid }));
  }
  if (mode === 'oids') {
    return lines
      .map((l) => l.replace(/^oid:/i, '').trim())
      .filter(Boolean)
      .map((oid) => ({ oid }));
  }
  if (mode === 'emails') {
    return lines.filter((l) => l.includes('@')).map((email) => ({ email }));
  }
  if (mode === 'phones') {
    return lines.map((phone) => ({ phone }));
  }
  // mixed: uid:N | oid:X | email | +phone
  return lines
    .map((line): RecipientRef | null => {
      const lower = line.toLowerCase();
      if (lower.startsWith('uid:')) {
        const uid = Number(line.slice(4).trim());
        return Number.isFinite(uid) && uid > 0 ? { uid } : null;
      }
      if (lower.startsWith('oid:')) {
        const oid = line.slice(4).trim();
        return oid ? { oid } : null;
      }
      if (line.includes('@')) return { email: line };
      if (/^\+?\d[\d\s-]+$/.test(line)) return { phone: line.replace(/\s+/g, '') };
      const asUid = Number(line);
      return Number.isFinite(asUid) && asUid > 0 ? { uid: asUid } : null;
    })
    .filter((r): r is RecipientRef => r != null);
};

const summarizeTally = (result: SendResult) => {
  const t = result.tally;
  if (!t) return `msgid ${result.msgid || 'ok'}`;
  const parts = [
    t.sent != null ? `${t.sent} sent` : null,
    t.failed ? `${t.failed} failed` : null,
    t.opted_out ? `${t.opted_out} opted out` : null,
    t.suppressed ? `${t.suppressed} suppressed` : null,
    t.no_address ? `${t.no_address} no address` : null,
    t.skipped ? `${t.skipped} skipped` : null,
  ].filter(Boolean);
  return parts.join(' · ') || `msgid ${result.msgid || 'ok'}`;
};

export default function CampaignSendForm({
  defaultChannels,
  lockedChannels = false,
  canOverride = false,
}: {
  defaultChannels: MessagingChannel[];
  lockedChannels?: boolean;
  canOverride?: boolean;
}) {
  const send = useCampaignSendMutation();
  const [recipientMode, setRecipientMode] = useState<RecipientMode>(
    defaultChannels.includes('email') && defaultChannels.length === 1
      ? 'emails'
      : defaultChannels.includes('sms') && defaultChannels.length === 1
        ? 'phones'
        : 'uid',
  );
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [channels, setChannels] = useState<MessagingChannel[]>(defaultChannels);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [html, setHtml] = useState('');
  const [reason, setReason] = useState('');
  const [respectPrefs, setRespectPrefs] = useState(true);
  // Empty = Hubtel, the one-way default. A phone line is chosen on purpose.
  const [smsLine, setSmsLine] = useState('');
  const smsLines = (useSmsLinesQuery().data?.lines || []).filter((l) => l.enabled && l.two_way);

  const toggleChannel = (id: MessagingChannel) => {
    if (lockedChannels) return;
    setChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleSend = async () => {
    const recipients = parseRecipients(recipientMode, recipientsRaw);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient', { richColors: true });
      return;
    }
    if (channels.length === 0) {
      toast.error('Select at least one channel', { richColors: true });
      return;
    }
    if (!body.trim()) {
      toast.error('Message body is required', { richColors: true });
      return;
    }
    if ((channels.includes('email') || channels.includes('in_app')) && !subject.trim()) {
      toast.error('Subject is required for email / in-app', { richColors: true });
      return;
    }

    try {
      const result = await send.mutateAsync({
        body: {
          recipients,
          channels,
          body: body.trim(),
          ...(subject.trim() ? { subject: subject.trim() } : {}),
          ...(html.trim() ? { html: html.trim() } : {}),
          respect_prefs: respectPrefs,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(channels.includes('sms') && smsLine ? { sms_line: smsLine } : {}),
        },
        idempotencyKey: generateIdempotencyKey(),
      });

      if (result.override_denied) {
        toast.warning('Override denied — message sent with prefs respected', {
          richColors: true,
        });
      }
      toast.success(summarizeTally(result), { richColors: true });
      setBody('');
      setHtml('');
    } catch (error: any) {
      toast.error(error?.message || 'Send failed', { richColors: true });
    }
  };

  const recipientHint =
    recipientMode === 'uid'
      ? 'One UID per line'
      : recipientMode === 'oids'
        ? 'One opaque OID per line'
        : recipientMode === 'emails'
          ? 'One email per line'
          : recipientMode === 'phones'
            ? 'One E.164 phone per line (+233…)'
            : 'uid:101 · oid:… · email · +phone (one per line)';

  return (
    <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-gray-500">Recipient type</label>
          <select
            value={recipientMode}
            onChange={(e) => setRecipientMode(e.target.value as RecipientMode)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="uid">User UIDs</option>
            <option value="oids">Organization OIDs</option>
            <option value="emails">Emails</option>
            <option value="phones">Phones</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500">Channels</label>
          <div className="flex flex-wrap gap-2 pt-1">
            {ALL_CHANNELS.map((ch) => {
              const on = channels.includes(ch.id);
              return (
                <button
                  key={ch.id}
                  type="button"
                  disabled={lockedChannels}
                  onClick={() => toggleChannel(ch.id)}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    on
                      ? 'border-blue-200 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-500'
                  } ${lockedChannels ? 'cursor-default opacity-80' : ''}`}
                >
                  {ch.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs text-gray-500">Recipients</label>
        <textarea
          value={recipientsRaw}
          onChange={(e) => setRecipientsRaw(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={recipientHint}
        />
      </div>

      {(channels.includes('email') || channels.includes('in_app')) && (
        <div>
          <label className="mb-1 block text-xs text-gray-500">Subject / title</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Heads up"
          />
        </div>
      )}

      {channels.includes('sms') && smsLines.length > 0 && (
        <div>
          <label className="mb-1 block text-xs text-gray-500">SMS from</label>
          <select
            aria-label="SMS from"
            value={smsLine}
            onChange={(e) => setSmsLine(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">FeelyFeely via Hubtel (one-way, replies are not collected)</option>
            {smsLines.map((l) => (
              <option key={l.id} value={l.id}>
                {smsLineLabel(l)}: replies come to the inbox
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs text-gray-500">Body (plain text)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Your listing was approved."
        />
      </div>

      {channels.includes('email') && (
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            HTML (optional — auto-derived from body if empty)
          </label>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="<p>…</p>"
          />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-gray-500">Audit reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="manual support outreach"
          />
        </div>
        <div className="flex flex-col justify-end gap-2 pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={respectPrefs}
              onChange={(e) => setRespectPrefs(e.target.checked)}
              className="rounded border-gray-300"
            />
            Respect opt-outs / prefs
          </label>
          {!respectPrefs && (
            <p className="text-xs text-amber-700">
              {canOverride
                ? 'Requires messaging.override — critical / transactional only.'
                : 'You lack messaging.override; the API will ignore force and set override_denied.'}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="button" disabled={send.isPending} onClick={handleSend} className="gap-2">
          {send.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Send
        </Button>
      </div>
    </div>
  );
}
